import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Check, Mail, Lock } from 'lucide-react';
import Navbar from './Navbar';

const API_BASE_URL = 'https://api.4fingerstudios.com';

// 15 minutes in milliseconds
const RATE_LIMIT_DURATION = 15 * 60 * 1000;

const GlitchText = ({ text, className }) => (
  <span className={`relative inline-block ${className} text-purple-500 animate-pulse`}>
    {text}
  </span>
);

const Input = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500/50" size={20} />
    <input
      {...props}
      className={`w-full bg-black/60 border border-purple-500/20 pl-10 p-3 text-slate-300 focus:border-purple-500 transition-colors duration-300 rounded ${className}`}
    />
  </div>
);

const ResetPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ text: '', type: '' });
  const [clientIp, setClientIp] = useState(''); // New state for client IP
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from query params
  const queryParams = new URLSearchParams(location.search);
  const tokenFromQuery = queryParams.get('token') || '';

  useEffect(() => {
    if (tokenFromQuery !== '') {
      setToken(tokenFromQuery);
    }
    // Fetch client IP on component mount
    const fetchClientIp = async () => {
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        setClientIp(ipData.ip);
      } catch (error) {
        console.error('Failed to fetch client IP:', error);
        setClientIp('Unknown'); // Fallback in case of error
      }
    };
    fetchClientIp();
  }, [tokenFromQuery]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } }
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  // Check if the user can send a reset request
  const canSendResetRequest = () => {
    const lastRequestTime = localStorage.getItem('lastResetRequestTime');
    const currentTime = Date.now();

    if (!lastRequestTime) return true;

    const timeElapsed = currentTime - parseInt(lastRequestTime, 10);
    return timeElapsed >= RATE_LIMIT_DURATION;
  };

  // Get remaining cooldown time in a readable format
  const getRemainingCooldown = () => {
    const lastRequestTime = localStorage.getItem('lastResetRequestTime');
    if (!lastRequestTime) return 0;

    const currentTime = Date.now();
    const timeElapsed = currentTime - parseInt(lastRequestTime, 10);
    const remainingMs = RATE_LIMIT_DURATION - timeElapsed;

    if (remainingMs <= 0) return 0;

    const minutes = Math.floor(remainingMs / (1000 * 60));
    const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedbackMessage({ text: '', type: '' });

    try {
      if (!email) {
        throw new Error('Please enter your email.');
      }

      if (!canSendResetRequest()) {
        const remainingTime = getRemainingCooldown();
        throw new Error(`Please wait ${remainingTime} before requesting another reset.`);
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Client-IP': clientIp || 'Unknown' // Include client IP in header
        },
        body: JSON.stringify({ email })
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || 'Failed to request password reset');
      }

      // Store the timestamp of this request
      localStorage.setItem('lastResetRequestTime', Date.now().toString());

      setFeedbackMessage({ text: responseText || 'Password reset link sent to your email!', type: 'success' });
      setEmail('');
    } catch (error) {
      setFeedbackMessage({ text: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedbackMessage({ text: '', type: '' });

    try {
      if (!newPassword) {
        throw new Error('Please enter a new password.');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Client-IP': clientIp || 'Unknown' // Include client IP in header
        },
        body: JSON.stringify({ token, newPassword })
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || 'Password reset failed');
      }

      setFeedbackMessage({ text: responseText || 'Password reset successfully!', type: 'success' });
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      setFeedbackMessage({ text: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-900 to-slate-900 text-purple-300 font-mono flex flex-col"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <div className="fixed top-0 left-0 w-full h-full bg-gradient-to-b from-gray-900 to-slate-900 opacity-50">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08)_0%,transparent_100%)] animate-pulse" />
      </div>

      <Navbar currentPage="Reset Password" />

      <main className="relative flex-grow pt-20 sm:pt-40 max-w-5xl mx-auto p-4 sm:p-6 space-y-20">
        <motion.section
          className="text-center space-y-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlitchText text="RESET PASSWORD" className="text-4xl sm:text-6xl md:text-7xl font-black" />
          <div className="h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent max-w-md mx-auto" />
          <p className="text-slate-300 text-base sm:text-lg">
            {token ? 'Enter your new password below.' : 'Enter your email to receive a password reset link.'}
          </p>
        </motion.section>

        <motion.section className="w-full max-w-md mx-auto px-4" variants={cardVariants}>
          <motion.div
            className="bg-black/60 border border-purple-500/20 p-6 sm:p-8 group hover:border-purple-500 transition-colors duration-300 rounded-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <GlitchText text={token ? 'NEW PASSWORD' : 'RESET REQUEST'} className="text-2xl sm:text-3xl font-bold mb-6" />

            {!token ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-purple-400 mb-2">Email:</label>
                  <Input
                    icon={Mail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full border border-purple-500 hover:bg-purple-500/20 py-3 rounded transition-colors uppercase tracking-wider"
                >
                  {isLoading ? 'SENDING...' : 'SEND RESET LINK'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-purple-400 mb-2">New Password:</label>
                  <Input
                    icon={Lock}
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full border border-purple-500 hover:bg-purple-500/20 py-3 rounded transition-colors uppercase tracking-wider"
                >
                  {isLoading ? 'RESETTING...' : 'RESET PASSWORD'}
                </button>
              </form>
            )}

            {feedbackMessage.text && (
              <p className={`text-sm mt-4 flex items-center gap-2 ${feedbackMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                {feedbackMessage.type === 'error' ? <AlertCircle size={16} /> : <Check size={16} />}
                {feedbackMessage.text}
              </p>
            )}

            <div className="text-sm text-slate-300 mt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-purple-500 hover:text-purple-400"
              >
                Back to Login
              </button>
            </div>
          </motion.div>
        </motion.section>
      </main>

      <footer className="relative bg-slate-900/80 text-slate-400 py-8 sm:py-12 border-t border-slate-800/50 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
            <div>
              <h4 className="text-purple-300 font-bold mb-4">4FingerStudios</h4>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://4fingerstudios.com" className="hover:text-purple-300 transition-colors duration-300">Home</a></li>
                <li><a href="https://4fingerstudios.com/contact" className="hover:text-purple-300 transition-colors duration-300">Contact</a></li>
                <li><a href="https://4fingerstudios.com/Privacy" className="hover:text-purple-300 transition-colors duration-300">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Contact Us</h4>
              <p className="text-sm mb-2">
                <span className="text-purple-300 mr-2">Email:</span> 4.finger.studios.com@gmail.com
              </p>
              <p className="text-sm">
                <span className="text-purple-300 mr-2">Support:</span> support@4fingerstudios.com
              </p>
            </div>
          </div>
          <div className="mt-8 sm:mt-12 pt-6 border-t border-slate-800/50 text-center text-xs">
            <p>© {new Date().getFullYear()} 4FingerStudios. All rights reserved.</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
      </footer>
    </motion.div>
  );
};

export default ResetPasswordPage;